import React, { useEffect, useState } from "react";
import { fmtDate, fmtDateTime } from "@/lib/dates";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Link2, Copy, Trash2, Eye, Mail } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

const fmt = (iso) => fmtDate(iso);
const fmtT = (iso) => fmtDateTime(iso);

function VisitLog({ link }) {
  const [open, setOpen] = useState(false);
  const log = [...(link.view_log || [])].reverse();
  const sent = link.sent_to || [];
  return (
    <div className="mt-2 text-xs">
      <button type="button" data-testid="share-visit-toggle" onClick={() => setOpen(!open)} className="text-brand underline-offset-2 hover:underline">
        {open ? "Hide" : "Show"} visit log{link.last_opened_at ? ` · last opened ${fmtT(link.last_opened_at)}` : " · not opened yet"}
      </button>
      {open && (
        <div data-testid="share-visit-log" className="mt-2 rounded-lg bg-cream p-2 space-y-1">
          {sent.length > 0 && <div className="text-muted-foreground">Emailed to {sent.map((s) => s.email).join(", ")}</div>}
          {log.length === 0 && <div className="text-muted-foreground">No visits recorded yet.</div>}
          {log.slice(0, 10).map((v, i) => (
            <div key={`${v.at}-${i}`} className="flex justify-between gap-2"><span>{v.what === "pdf" ? "Opened Evidence Pack PDF" : "Opened inspector page"}</span><span className="text-muted-foreground">{fmtT(v.at)}</span></div>
          ))}
        </div>
      )}
    </div>
  );
}

export function InspectorLinksDialog({ open, onOpenChange }) {
  const [links, setLinks] = useState([]);
  const [days, setDays] = useState("7");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [emailFor, setEmailFor] = useState(null);
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const sendEmail = async (e) => {
    e.preventDefault(); setSending(true);
    try {
      await api.post(`/compliance/share-links/${emailFor.id}/email`, { to, message });
      toast.success(`Inspector link emailed to ${to}`);
      setEmailFor(null); setTo(""); setMessage(""); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const load = () => api.get("/compliance/share-links").then((r) => setLinks(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/compliance/share-links", { days: Number(days), note });
      await navigator.clipboard?.writeText(data.url).catch(() => {});
      toast.success("Inspector link created and copied");
      setNote(""); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const copy = async (url) => { await navigator.clipboard?.writeText(url); toast.success("Link copied"); };
  const revoke = async (id) => {
    try { await api.delete(`/compliance/share-links/${id}`); toast.success("Link revoked"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const active = links.filter((l) => !l.revoked && !l.expired);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="inspector-links-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-brand">Share with an inspector</DialogTitle>
          <DialogDescription>Create a time-limited link so a CQC inspector can view your live Evidence Pack without logging in. Revoke it at any time.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
          <div><Label>Note (optional)</Label><Input data-testid="share-note" placeholder="e.g. CQC visit 14 Oct" value={note} onChange={(e) => setNote(e.target.value)} /></div>
          <div><Label>Expires in</Label>
            <Select value={days} onValueChange={setDays}><SelectTrigger data-testid="share-days" className="w-[120px]"><SelectValue /></SelectTrigger>
              <SelectContent>{["1", "3", "7", "14", "30"].map((d) => <SelectItem key={d} value={d}>{d} day{d !== "1" ? "s" : ""}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
        <Button data-testid="share-create-btn" onClick={create} disabled={busy} className="rounded-full bg-coral hover:bg-coral-hover text-white"><Link2 className="h-4 w-4 mr-2" />{busy ? "Creating…" : "Create link & copy"}</Button>
        <div className="space-y-2 mt-2 max-h-64 overflow-auto">
          {active.length === 0 && <p className="text-sm text-muted-foreground">No active links.</p>}
          {active.map((l, i) => (
            <div key={l.id} data-testid={`share-link-${i}`} className="rounded-lg border border-cream-deep p-3 text-sm flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{l.note || "Inspector link"}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-2">Expires {fmt(l.expires_at)} · <Eye className="h-3 w-3" />{l.views} PDF view{l.views !== 1 ? "s" : ""}</div>
                <VisitLog link={l} />
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="outline" data-testid={`share-email-${i}`} onClick={() => { setEmailFor(l); setTo(""); }} className="h-8 rounded-full"><Mail className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="outline" data-testid={`share-copy-${i}`} onClick={() => copy(l.url)} className="h-8 rounded-full"><Copy className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="ghost" data-testid={`share-revoke-${i}`} onClick={() => revoke(l.id)} className="h-8 rounded-full text-rag-red"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
        {emailFor && (
          <form onSubmit={sendEmail} data-testid="share-email-form" className="mt-3 rounded-xl bg-cream border border-cream-deep p-4 space-y-3">
            <div className="text-sm font-semibold text-brand flex items-center gap-2"><Mail className="h-4 w-4" />Email this link{emailFor.note ? ` — ${emailFor.note}` : ""}</div>
            <div><Label>Inspector's email</Label><Input data-testid="share-email-to" type="email" required placeholder="inspector@cqc.org.uk" value={to} onChange={(e) => setTo(e.target.value)} /></div>
            <div><Label>Short message (optional)</Label><Textarea data-testid="share-email-message" rows={2} maxLength={300} placeholder="e.g. As discussed on our call, here is our live Evidence Pack ahead of your visit." value={message} onChange={(e) => setMessage(e.target.value)} /></div>
            <div className="flex gap-2">
              <Button type="submit" data-testid="share-email-send" disabled={sending} size="sm" className="rounded-full bg-brand hover:bg-brand-hover text-white">{sending ? "Sending…" : "Send branded email"}</Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setEmailFor(null)} className="rounded-full">Cancel</Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
